import { CSSProperties, Dispatch, Fragment, FunctionComponent, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import { navigate } from '@/utils/history';
import { t } from '@/utils/i18n';
import { getParams } from '@/utils/params';
import { processErrorForCustomData, trackError } from '@/utils/raygun';
import { usePriceDetails } from '@/hooks/usePriceDetails';
import { getIsUnprintableConfiguration } from '@/model/configuration/getIsUnprintableConfiguration';
import { ErrorTypes } from '@/model/product/errorTypes';
import { isSaveProductError } from '@/model/product/getProductReference';
import { getProductSavingMessage } from '@/model/product/getProductSavingMessage';
import { addToBasketAction } from '@/redux/basket/actions/addToBasketAction';
import { getMinimumBasketItemCount } from '@/redux/basket/getters/getMinimumBasketItemCount';
import { getCurrencyId } from '@/redux/core/getters/getCurrencyId';
import { useReduxDraftDispatch } from '@/redux/draft/draftDispatch';
import { getCurrentAppearance } from '@/redux/draft/getters/getCurrentAppearance';
import { getIsCurrentProductTypeAndAppearanceOutOfStock } from '@/redux/draft/getters/getIsCurrentProductTypeAndAppearanceOutOfStock';
import { selectConfigurations } from '@/redux/draft/getters/selectConfigurations';
import { saveProductAction } from '@/redux/product/actions/saveProductAction';
import { trackAddToCart } from '@/redux/product/utils/trackAddToCart';
import { selectIsBlankVerbotenProductType } from '@/redux/productTypes/getters/selectIsBlankVerbotenProductType';
import { StoreDispatch } from '@/redux/types';
import { Button } from '@/components/common/Button';
import { Warning } from '@/components/common/Warning';
import { Cart } from '@/components/common/icons/Cart';
import { setValidationAction } from '@/components/draft/draftState/actions/setValidationAction';
import { SaveDialogStateAction } from '../SaveDialog';

export const logSaveProductError = async (error: unknown, errorType: ErrorTypes) => {
  const apiError = processErrorForCustomData(error);
  trackError(`Failed to create product [${errorType}]`, { apiError });
};

interface AddToBasketProps {
  dispatch: Dispatch<SaveDialogStateAction>;
  onAdded?: () => void;
  className?: string;
}

export const AddToBasket: FunctionComponent<AddToBasketProps> = ({
  dispatch,
  onAdded,
  className,
}) => {
  const storeDispatch = useDispatch<StoreDispatch>();
  const draftDispatch = useReduxDraftDispatch();
  const appearance = useSelector(getCurrentAppearance);
  const configurations = useSelector(selectConfigurations);
  const minItemCount = useSelector(getMinimumBasketItemCount);
  const isOutOfStock = useSelector(getIsCurrentProductTypeAndAppearanceOutOfStock);
  const isBlankVerbotenProductType = useSelector(selectIsBlankVerbotenProductType);
  const isBlankVerboten = useMemo(
    () => isBlankVerbotenProductType && configurations.every(getIsUnprintableConfiguration),
    [isBlankVerbotenProductType, configurations]
  );

  const currencyId = useSelector(getCurrencyId);
  const [productType, discountBreakdown, selectedSizes, selectedSizesItemCount] = usePriceDetails();

  const handleAddToBasket = async () => {
    dispatch({ type: 'save-start', payload: configurations });

    try {
      const productReference = await storeDispatch(
        saveProductAction(() => dispatch({ type: 'save-update' }))
      );
      await storeDispatch(addToBasketAction(selectedSizes, productReference, onAdded));
      trackAddToCart(
        productType.id,
        appearance.id,
        configurations,
        selectedSizes,
        discountBreakdown.product,
        discountBreakdown.currentDiscount,
        currencyId
      );

      dispatch({ type: 'save-success' });
      if (getParams().openBasket) {
        navigate('checkout');
      }
    } catch (error) {
      const [errorMessage, errorType] = getProductSavingMessage(error);
      dispatch({ type: 'save-error', payload: error });

      if (isSaveProductError(error)) {
        const configurationId = error.configurationId;
        if (configurationId) {
          draftDispatch(setValidationAction({ [configurationId]: { api: errorMessage } }));
        }
      }

      logSaveProductError(error, errorType);
    }
  };

  if (isOutOfStock) {
    return <Warning className="panel__cta" message={t('general.outOfStock')} />;
  }

  if (isBlankVerboten) {
    return <Warning className="panel__cta" message={t('basket.productMustHavePrint')} />;
  }

  const unreachedMinimum = selectedSizesItemCount < minItemCount && minItemCount > 1;
  return (
    <Button
      data-testid="add-to-basket"
      className={cn('panel__cta', className, { 'panel__cta--progress': unreachedMinimum })}
      onClick={handleAddToBasket}
      disabled={selectedSizesItemCount < minItemCount}
      style={{ '--progress': (selectedSizesItemCount / minItemCount) * 100 + '%' } as CSSProperties}
    >
      {unreachedMinimum ? (
        t('basket.unreachedMinimum', String(selectedSizesItemCount), String(minItemCount))
      ) : (
        <Fragment>
          <Cart />
          {t('basket.addToBasket')}
        </Fragment>
      )}
    </Button>
  );
};
